import React, { useState } from "react";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import { RiArrowDownSLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";

// A toolbar dropdown (Admin · Table Toolbar 10600:635, the "Status: All" filters): "Label: choice"
// over a menu of the choices. Picking the current choice again changes nothing. `hint` explains a
// disabled button in a tooltip. Keyboard focus shows D22's ring, not MUI's grey focus ripple.
const disabledLook = { color: tokens.text.disabled, borderColor: tokens.border.default };
export const buttonSx = {
  height: 40,
  px: "16px",
  gap: "8px",
  borderRadius: "10px",
  border: `1px solid ${tokens.border.strong}`,
  color: tokens.text.primary,
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 500,
  letterSpacing: "0.5px",
  "&:hover": { bgcolor: tokens.hover },
  "&.Mui-disabled": disabledLook,
  ...focusRing,
};
// 047 D74: a disabled button with a hint stays in the tab order (`aria-disabled`, not `disabled`,
// which MUI cannot focus or hover), wears the disabled look, and the tooltip describes it, so the
// reason reaches the keyboard and a screen reader as well as the mouse.
const explainedSx = { ...buttonSx, ...disabledLook, cursor: "default", "&:hover": { bgcolor: "transparent" } };

const ToolbarMenu = ({ id, label, value, options, onChange, disabled = false, hint }) => {
  const [anchor, setAnchor] = useState(null);
  const current = options.find((o) => o.key === value) || options[0];
  const explained = disabled && Boolean(hint);
  const choose = (key) => {
    setAnchor(null);
    if (key !== value) onChange(key);
  };
  const button = (
    <Button
      id={`${id}-button`}
      onClick={(e) => !disabled && setAnchor(e.currentTarget)}
      disabled={disabled && !explained}
      aria-disabled={explained || undefined}
      disableFocusRipple
      disableRipple={explained}
      sx={explained ? explainedSx : buttonSx}
      aria-haspopup="menu"
      aria-expanded={Boolean(anchor)}
      aria-controls={anchor ? `${id}-menu` : undefined}
    >
      {label}: {current.label}
      <RiArrowDownSLine size={24} />
    </Button>
  );
  return (
    <>
      {explained ? (
        <Tooltip title={hint} describeChild>
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <Menu
        id={`${id}-menu`}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ list: { "aria-labelledby": `${id}-button` } }}
      >
        {options.map((o) => (
          <MenuItem key={o.key} selected={o.key === value} onClick={() => choose(o.key)}>
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ToolbarMenu;
